<template>
  <v-card elevation="0" flat tile>
    <v-card-text>
      <v-row justify="space-around" v-show="isAdmin">
        <v-col cols="12" md="5">
          <v-file-input
            ref="media"
            accept="image/*"
            :label="$t('common:media')"
            show-size
            @change="uploadMedia"
            v-model="media"
            :rules="[
              ruleMaxFileSize(fileSizeLimit,$t('utils:rules.filesize', {size : '20 MB'}))
              ]"
          >
            <template v-slot:append>
              <v-fade-transition leave-absolute>
                <v-progress-circular
                    v-show="isMediaLoading"
                    size="24"
                    color="primary"
                    indeterminate
                ></v-progress-circular>
              </v-fade-transition>
            </template>
          </v-file-input>
          <v-btn
              color="red"
              class="mr-3 mb-3 mb-md-0 white--text"
              outlined
              elevation="0"
              @click="resetImage('media')"
          >
            <v-icon class="mr-2">no_photography</v-icon>
            {{$t('common:reset')}}
          </v-btn>
        </v-col>
        <v-col cols="12" md="5">
          <ImageViewer
            maxHeight="500"
            maxWidth="500"
            ref="mediaViewer"
          />
        </v-col>
      </v-row>
      <v-row justify="space-around">
        <v-col cols="12" md="5">
          <v-file-input
            ref="background"
            accept="image/*"
            :label="$t('display/elevator:edit.labels.image-tab.background')"
            show-size
            @change="uploadBackground"
            v-model="background"
            :rules="[
              ruleMaxFileSize(fileSizeLimit,$t('utils:rules.filesize', {size : '20 MB'}))
              ]"
          >
            <template v-slot:append>
              <v-fade-transition leave-absolute>
                <v-progress-circular
                    v-show="isBackgroundLoading"
                    size="24"
                    color="primary"
                    indeterminate
                ></v-progress-circular>
              </v-fade-transition>
            </template>
          </v-file-input>
          <!-- Apply to all -->
          <v-checkbox
            color="secondary"
            v-show="isAdmin"
            v-model="applyToAllBackground"
            :label="$t('display/elevator:edit.labels.image-tab.apply-to-all')"
          />

          <v-btn
              color="red"
              class="mr-3 mb-3 mb-md-0 white--text"
              outlined
              elevation="0"
              @click="resetImage('background')"
          >
            <v-icon class="mr-2">no_photography</v-icon>
            {{$t('common:reset')}}
          </v-btn>
        </v-col>
        <v-col cols="12" md="5">
          <ImageViewer
            maxHeight="500"
            maxWidth="500"
            ref="backgroundViewer"
          />
        </v-col>
      </v-row>
      <v-row justify="space-around">
        <v-col cols="12" md="5">
          <v-file-input
            ref="logo"
            accept="image/*"
            :label="$t('display/elevator:edit.labels.image-tab.logo')"
            show-size
            @change="uploadLogo"
            v-model="logo"
            :rules="[
              ruleMaxFileSize(fileSizeLimit,$t('utils:rules.filesize', {size : '20 MB'}))
              ]"
          >
            <template v-slot:append>
              <v-fade-transition leave-absolute>
                <v-progress-circular
                    v-show="isLogoLoading"
                    size="24"
                    color="primary"
                    indeterminate
                ></v-progress-circular>
              </v-fade-transition>
            </template>
          </v-file-input>
          <!-- Apply to all -->
          <v-checkbox
            v-show="isAdmin"
            color="secondary"
            v-model="applyToAllLogo"
            :label="$t('display/elevator:edit.labels.image-tab.apply-to-all')"
          />
          <v-btn
              color="red"
              class="mr-3 mb-3 mb-md-0 white--text"
              outlined
              elevation="0"
              @click="resetImage('logo')"
          >
            <v-icon class="mr-2">no_photography</v-icon>
            {{$t('common:reset')}}
          </v-btn>
        </v-col>
        <v-col cols="12" md="5">
          <ImageViewer
            maxHeight="500"
            maxWidth="500"
            ref="logoViewer"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import ElevatorService from '@/modules/display/services/elevator-service';
import ImageViewer from '@/modules/core/components/DocumentViewer/ImageViewer';
import {Role} from "@/modules/core/data/roles";
import {ruleRequired, ruleRequiredMultiple, ruleMaxFileSize} from '@/modules/core/services/rules';
import Utils from '@/modules/core/services/utils';

export default {
  name: "ImageTab",
  data() {
    return {
      background: null,
      applyToAllBackground: false,
      logo: null,
      applyToAllLogo: false,
      media: null,
      isLogoLoading: false,
      isBackgroundLoading: false,
      isMediaLoading: false
    }
  },
  components: {
    ImageViewer
  },
  computed: {
    fileSizeLimit() {
      return process.env.VUE_APP_UPLOAD_SIZE_DOCUMENT;
    },
    isAdmin() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes(Role.Admin);
      }
      return false;
    },
    currentUser() {
      return this.$store.state.auth.user;
    }
  },
  methods: {
    ruleRequired, ruleRequiredMultiple,
    ruleMaxFileSize,
    showMedia() {
      let mediaId = this.$store.state.display.elevatorForm.elevator.mediaId;
      if (!this.$refs.mediaViewer) return;
      this.$refs.mediaViewer.reset();

      if (mediaId) {
        ElevatorService.loadMedia(
            mediaId,
            result => this.$refs.mediaViewer.initViewer({"name": '', "src": result})
        );
      } else {
        ElevatorService.loadImage(
            '/static/assets/images/preview4x3.png',
            result => this.$refs.mediaViewer.initViewer({"name": '', "src": result})
        );
      }
    },
    showBackground() {
      let urlBackground = this.$store.state.display.elevatorForm.elevator.urlBackground;
      if (!this.$refs.backgroundViewer) return;

      this.$refs.backgroundViewer.reset();
      if (urlBackground) {
        ElevatorService.loadImage(
          urlBackground,
          result => this.$refs.backgroundViewer.initViewer({"name": '', "src": result})
        );
      }

    },
    showLogo() {
      let urlLogo = this.$store.state.display.elevatorForm.elevator.urlLogo;
      if (!this.$refs.logoViewer) return;

      this.$refs.logoViewer.reset();
      if (urlLogo) {
        ElevatorService.loadImage(
          urlLogo,
          result => this.$refs.logoViewer.initViewer({"name": '', "src": result})
        );
      }
    },
    uploadBackground(file, isReset) {
      if (!isReset && (typeof this.background === 'undefined' || this.background === null)) {
        return;
      }

      let currentElevatorId = this.$store.state.display.elevatorForm.elevator.id;
      let store = this.$store;
      let additionalElevators = [];
      if (this.$store.state.display.elevatorForm.elevator.additionalElevators) {
        additionalElevators = this.$store.state.display.elevatorForm.elevator.additionalElevators;
      }
      this.isBackgroundLoading = !isReset;

      ElevatorService.uploadBackground(
        currentElevatorId,
        isReset ? null : this.background,
        this.applyToAllBackground,
        additionalElevators,
        (response) => {
          this.isBackgroundLoading = false;
          if (currentElevatorId == null) {
            store.commit('display/elevatorForm/setElevatorId', response.data);
          }

          if (!isReset) {
            store.commit('display/elevatorForm/updateBackgroundUrl', '/static/elevator/background/' + response.data);
            this.showBackground();
          }
        },
        (error) => {
          this.isBackgroundLoading = false;
          this.$logger.error("Error at background uploading: " + error);
          Utils.showFailureSnackbar(!isReset ?
              this.$t('display/elevator:edit.snackbar.image-uploading-error') :
              this.$t('display/elevator:edit.snackbar.image-reset-error')
          );
        }
      )
    },
    uploadLogo(file, isReset = false) {
      if (!isReset && (typeof this.logo === 'undefined' || this.logo === null)) {
        return;
      }

      let currentElevatorId = this.$store.state.display.elevatorForm.elevator.id;
      let store = this.$store;
      let additionalElevators = [];
      if (this.$store.state.display.elevatorForm.elevator.additionalElevators) {
        additionalElevators = this.$store.state.display.elevatorForm.elevator.additionalElevators;
      }
      this.isLogoLoading = !isReset;

      ElevatorService.uploadLogo(
        currentElevatorId,
        isReset ? null : this.logo,
        this.applyToAllLogo,
        additionalElevators,
        (response) => {
          this.isLogoLoading = false;
          if (currentElevatorId == null) {
            store.commit('display/elevatorForm/setElevatorId', response.data);
          }

          if (!isReset) {
            store.commit('display/elevatorForm/updateLogoUrl', '/static/elevator/logo/' + response.data);
            this.showLogo();
          }
        },
        (error) => {
          this.isLogoLoading = false;
          this.$logger.error("Error at logo uploading: " + error);
          Utils.showFailureSnackbar(!isReset ?
              this.$t('display/elevator:edit.snackbar.image-uploading-error') :
              this.$t('display/elevator:edit.snackbar.image-reset-error')
          );
        }
      )
    },
    uploadMedia() {

      if (typeof this.media === 'undefined' || this.media === null) {
        return;
      }

      let currentElevatorId = this.$store.state.display.elevatorForm.elevator.id;
      let store = this.$store;
      this.isMediaLoading = true;
      ElevatorService.uploadMedia(
        currentElevatorId,
        this.media,
        (response) => {
          this.isMediaLoading = false;
          if (currentElevatorId == null) {
            store.commit('display/elevatorForm/setElevatorId', response.data.body.elevatorId);
          }

          store.commit('display/elevatorForm/updateMediaType', response.data.body.mediaType);
          store.commit('display/elevatorForm/updateMediaId', response.data.body.id);
          this.showMedia();
        },
        (error) => {
          this.$logger.error("Error at media uploading: " + error);
          Utils.showFailureSnackbar(this.$t('display/elevator:edit.snackbar.image-uploading-error'));
        }
      )
    },
    loadImages() {
      this.showBackground();
      this.showLogo();
      this.showMedia();
    },
    resetImage(type) {
      let store = this.$store;
      let defaultImage = '/static/assets/images/preview_logo.png';
      if (type === 'background') {
        store.commit('display/elevatorForm/updateBackgroundUrl', defaultImage);
        this.showBackground();
        this.uploadBackground(null, true);
      } else if (type === 'logo') {
        store.commit('display/elevatorForm/updateLogoUrl', defaultImage);
        this.showLogo();
        this.uploadLogo(null, true);
      } else if (type === 'media') {
        store.commit('display/elevatorForm/updateMediaType', '');
        store.commit('display/elevatorForm/updateMediaId', '');
        this.showMedia();
      }
    }

  },
  created() {
    this.$store.commit('display/elevatorForm/addListener', this.loadImages);
  }
}
</script>

<style scoped>
</style>